import React, { useState, useEffect } from 'react';
import { contentAPI } from '../../services/api';
import { FaUserPlus, FaTrash, FaUserTie, FaUsers, FaPlus, FaEdit } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';

const CommitteesSection = () => {
  const { language, t } = useLanguage();
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCommittee, setSelectedCommittee] = useState(null);

  // Committee Management
  const [committeeFormData, setCommitteeFormData] = useState({
    name: '',
    name_ar: '',
    icon: '🏛️',
    color: '#1a365d',
    display_order: 0
  });
  const [editingCommittee, setEditingCommittee] = useState(null);
  const [showCommitteeModal, setShowCommitteeModal] = useState(false);

  // Member Management
  const [memberFormData, setMemberFormData] = useState({
    name: '',
    name_ar: '',
    role: '',
    role_ar: '',
    image: null,
    display_order: 0
  });
  const [editingMember, setEditingMember] = useState(null);

  useEffect(() => {
    loadCommittees();
  }, []);

  const loadCommittees = async () => {
    try {
      setLoading(true);
      const response = await contentAPI.getCommittees();
      const data = response.data || response || [];
      setCommittees(data);

      if (data.length > 0) {
        if (!selectedCommittee) {
          setSelectedCommittee(data[0]);
        } else {
          const updated = data.find(c => c.id === selectedCommittee.id);
          if (updated) setSelectedCommittee(updated);
          else setSelectedCommittee(data[0]);
        }
      } else {
        setSelectedCommittee(null);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // COMMITTEE CRUD
  const handleCommitteeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCommittee) {
        await contentAPI.updateCommittee(editingCommittee.id, committeeFormData);
      } else {
        await contentAPI.createCommittee(committeeFormData);
      }
      setCommitteeFormData({ name: '', name_ar: '', icon: '🏛️', color: '#1a365d', display_order: 0 });
      setEditingCommittee(null);
      setShowCommitteeModal(false);
      loadCommittees();
      alert(language === 'ar' ? 'تم حفظ اللجنة!' : 'Comité enregistré !');
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const handleDeleteCommittee = async (id) => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه اللجنة وكل أعضائها؟' : 'Supprimer ce comité et tous ses membres ?')) {
      try {
        await contentAPI.deleteCommittee(id);
        if (selectedCommittee?.id === id) setSelectedCommittee(null);
        loadCommittees();
      } catch (error) {
        alert('Erreur: ' + error.message);
      }
    }
  };

  const openCommitteeEdit = (committee) => {
    setEditingCommittee(committee);
    setCommitteeFormData({
      name: committee.name || '',
      name_ar: committee.name_ar || '',
      icon: committee.icon || '🏛️',
      color: committee.color || '#1a365d',
      display_order: committee.display_order || 0
    });
    setShowCommitteeModal(true);
  };

  // MEMBER CRUD
  const handleMemberSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCommittee) return;
    try {
      const formData = new FormData();
      formData.append('committee_id', selectedCommittee.id);
      formData.append('name', memberFormData.name);
      formData.append('name_ar', memberFormData.name_ar);
      formData.append('role', memberFormData.role);
      formData.append('role_ar', memberFormData.role_ar);
      formData.append('display_order', memberFormData.display_order);

      if (memberFormData.image) {
        formData.append('image', memberFormData.image);
      } else if (editingMember?.image_url) {
        formData.append('image_url', editingMember.image_url);
      }

      if (editingMember) {
        await contentAPI.updateCommitteeMember(editingMember.id, formData);
      } else {
        await contentAPI.createCommitteeMember(formData);
      }

      setMemberFormData({ name: '', name_ar: '', role: '', role_ar: '', image: null, display_order: 0 });
      setEditingMember(null);
      loadCommittees();
      alert(language === 'ar' ? 'تم حفظ العضو!' : 'Membre enregistré !');
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setMemberFormData({
      name: member.name || '',
      name_ar: member.name_ar || '',
      role: member.role || '',
      role_ar: member.role_ar || '',
      display_order: member.display_order || 0,
      image: null
    });
  };

  const handleDeleteMember = async (memberId) => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا العضو؟' : 'Supprimer ce membre ?')) {
      try {
        await contentAPI.deleteCommitteeMember(memberId);
        loadCommittees();
      } catch (error) {
        alert('Erreur: ' + error.message);
      }
    }
  };

  if (loading && committees.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={styles.title}>🏛️ {language === 'ar' ? 'إدارة اللجان' : 'Gestion des Comités'}</h1>
        <button
          onClick={() => {
            setEditingCommittee(null);
            setCommitteeFormData({ name: '', name_ar: '', icon: '🏛️', color: '#1a365d', display_order: committees.length + 1 });
            setShowCommitteeModal(true);
          }}
          style={styles.addMainBtn}
        >
          <FaPlus style={{ marginRight: '8px' }} />
          {language === 'ar' ? 'إضافة لجنة' : 'Nouveau Comité'}
        </button>
      </div>

      <div style={styles.committeesTabs}>
        {committees.map(committee => (
          <div key={committee.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => setSelectedCommittee(committee)}
              style={{
                ...styles.tabBtn,
                ...(selectedCommittee?.id === committee.id ? styles.tabBtnActive : {})
              }}
            >
              {language === 'ar' ? committee.name_ar : committee.name}
            </button>
            {selectedCommittee?.id === committee.id && (
              <div style={styles.tabActions}>
                <button onClick={() => openCommitteeEdit(committee)} style={styles.tabActionBtn} title="Modifier le comité">
                  <FaEdit size={10} />
                </button>
                <button onClick={() => handleDeleteCommittee(committee.id)} style={{ ...styles.tabActionBtn, color: '#ff4d4d' }} title="Supprimer le comité">
                  <FaTrash size={10} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedCommittee ? (
        <div style={styles.managementGrid}>
          {/* Member Form Card */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>
              <FaUserPlus style={{ [language === 'ar' ? 'marginLeft' : 'marginRight']: '10px' }} />
              {editingMember
                ? (language === 'ar' ? 'تعديل عضو' : 'Modifier le membre')
                : (language === 'ar' ? 'إضافة عضو جديد' : 'Ajouter un membre')}
            </h2>
            <form onSubmit={handleMemberSubmit} style={styles.form}>
              <div style={styles.statusBox}>
                <span style={styles.label}>{language === 'ar' ? 'حالة النشر:' : 'Statut de publication:'}</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await contentAPI.updateCommittee(selectedCommittee.id, { is_active: selectedCommittee.is_active === 1 ? 0 : 1 });
                      loadCommittees();
                    } catch (e) { alert('Erreur: ' + e.message); }
                  }}
                  style={{
                    ...styles.statusBadge,
                    background: selectedCommittee.is_active === 1 ? '#28a745' : '#6c757d',
                  }}
                >
                  {selectedCommittee.is_active === 1
                    ? (language === 'ar' ? 'منشورة' : 'Publiée')
                    : (language === 'ar' ? 'مسودة' : 'Brouillon')}
                </button>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nom (FR)</label>
                  <input
                    type="text"
                    value={memberFormData.name}
                    onChange={e => setMemberFormData({ ...memberFormData, name: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>الاسم (AR)</label>
                  <input
                    type="text"
                    value={memberFormData.name_ar}
                    onChange={e => setMemberFormData({ ...memberFormData, name_ar: e.target.value })}
                    style={{ ...styles.input, textAlign: 'right' }}
                    required
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Rôle (FR)</label>
                  <input
                    type="text"
                    value={memberFormData.role}
                    onChange={e => setMemberFormData({ ...memberFormData, role: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>الخطة (AR)</label>
                  <input
                    type="text"
                    value={memberFormData.role_ar}
                    onChange={e => setMemberFormData({ ...memberFormData, role_ar: e.target.value })}
                    style={{ ...styles.input, textAlign: 'right' }}
                    required
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{language === 'ar' ? 'الصورة' : 'Photo'}</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => setMemberFormData({ ...memberFormData, image: e.target.files[0] })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{language === 'ar' ? 'الترتيب' : 'Ordre'}</label>
                  <input
                    type="number"
                    value={memberFormData.display_order}
                    onChange={e => setMemberFormData({ ...memberFormData, display_order: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" style={{ ...styles.submitBtn, flex: 1, background: editingMember ? '#007bff' : '#28a745' }}>
                  {editingMember ? (language === 'ar' ? 'حفظ التعديلات' : 'Sauvegarder') : (language === 'ar' ? 'إضافة' : 'Ajouter')}
                </button>
                {editingMember && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingMember(null);
                      setMemberFormData({ name: '', name_ar: '', role: '', role_ar: '', image: null, display_order: 0 });
                    }}
                    style={{ ...styles.submitBtn, background: '#6c757d' }}
                  >
                    {language === 'ar' ? 'إلغاء' : 'Annuler'}
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Members List Card */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>
              <FaUsers style={{ [language === 'ar' ? 'marginLeft' : 'marginRight']: '10px' }} />
              {language === 'ar' ? 'قائمة الأعضاء' : 'Liste des Membres'} ({selectedCommittee.members?.length || 0})
            </h2>
            <div style={styles.membersList}>
              {(!selectedCommittee.members || selectedCommittee.members.length === 0) ? (
                <div style={styles.emptyText}>
                  {language === 'ar' ? 'لا يوجد أعضاء في هذه اللجنة' : 'Aucun membre dans ce comité'}
                </div>
              ) : (
                selectedCommittee.members.map(member => (
                  <div key={member.id} style={styles.memberItem}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                      <div style={styles.memberAvatar}>
                        {member.image_url ? (
                          <img src={member.image_url} alt="" style={styles.avatarImg} />
                        ) : (
                          <FaUserTie size={18} color="#999" />
                        )}
                      </div>
                      <div style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                        <div style={styles.memberName}>
                          {language === 'ar' ? member.name_ar : member.name}
                        </div>
                        <div style={styles.memberRole}>
                          {language === 'ar' ? member.role_ar : member.role}
                        </div>
                      </div>
                    </div>
                    <div style={styles.memberActions}>
                      <button onClick={() => handleEditMember(member)} style={styles.editBtnSmall} title="Modifier">
                        <FaEdit size={14} />
                      </button>
                      <button onClick={() => handleDeleteMember(member.id)} style={styles.deleteBtnSmall} title="Supprimer">
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.emptyState}>
          {language === 'ar' ? 'الرجاء اختيار أو إنشاء لجنة' : 'Veuillez sélectionner ou créer un comité'}
        </div>
      )}

      {/* COMMITTEE MODAL */}
      {showCommitteeModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>
              {editingCommittee ? (language === 'ar' ? 'تعديل اللجنة' : 'Modifier le comité') : (language === 'ar' ? 'لجنة جديدة' : 'Nouveau comité')}
            </h2>
            <form onSubmit={handleCommitteeSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nom (FR)</label>
                <input
                  type="text"
                  value={committeeFormData.name}
                  onChange={e => setCommitteeFormData({ ...committeeFormData, name: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>الاسم (AR)</label>
                <input
                  type="text"
                  value={committeeFormData.name_ar}
                  onChange={e => setCommitteeFormData({ ...committeeFormData, name_ar: e.target.value })}
                  style={{ ...styles.input, textAlign: 'right' }}
                  required
                />
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Icon (emoji)</label>
                  <input
                    type="text"
                    value={committeeFormData.icon}
                    onChange={e => setCommitteeFormData({ ...committeeFormData, icon: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Couleur</label>
                  <input
                    type="color"
                    value={committeeFormData.color}
                    onChange={e => setCommitteeFormData({ ...committeeFormData, color: e.target.value })}
                    style={{ ...styles.input, padding: '2px', height: '40px' }}
                  />
                </div>
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.saveBtn}>
                  {language === 'ar' ? 'حفظ' : 'Enregistrer'}
                </button>
                <button type="button" onClick={() => setShowCommitteeModal(false)} style={styles.cancelBtnModal}>
                  {language === 'ar' ? 'إلغاء' : 'Annuler'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  title: { fontSize: '28px', fontWeight: 'bold', color: '#1a365d', margin: 0 },
  addMainBtn: { display: 'flex', alignItems: 'center', padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
  committeesTabs: { display: 'flex', gap: '8px', marginBottom: '30px', overflowX: 'auto', padding: '10px 0' },
  tabBtn: { padding: '12px 24px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap', transition: 'all 0.2s', color: '#64748b' },
  tabBtnActive: { background: '#1a365d', borderColor: '#1a365d', color: 'white', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  tabActions: { position: 'absolute', top: '-10px', right: '-5px', display: 'flex', gap: '2px' },
  tabActionBtn: { width: '22px', height: '22px', borderRadius: '50%', border: 'none', background: 'white', color: '#1a365d', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  managementGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' },
  card: { background: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' },
  cardTitle: { fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', display: 'flex', alignItems: 'center', color: '#1e293b' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#64748b' },
  input: { padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' },
  statusBox: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '10px' },
  statusBadge: { padding: '4px 12px', borderRadius: '20px', border: 'none', color: 'white', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer' },
  submitBtn: { padding: '12px', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', transition: 'opacity 0.2s' },
  membersList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  memberItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' },
  memberAvatar: { width: '40px', height: '40px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  memberName: { fontWeight: '700', color: '#1e293b', fontSize: '15px' },
  memberRole: { fontSize: '12px', color: '#64748b', fontWeight: '500' },
  memberActions: { display: 'flex', gap: '6px' },
  editBtnSmall: { padding: '6px', background: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  deleteBtnSmall: { padding: '6px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  emptyState: { padding: '80px', textAlign: 'center', background: 'white', borderRadius: '16px', color: '#94a3b8', fontSize: '16px', border: '1px dashed #cbd5e1' },
  emptyText: { textAlign: 'center', padding: '40px', color: '#94a3b8', fontStyle: 'italic' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px' },
  spinner: { width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #c41e3a', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modalContent: { background: 'white', padding: '32px', borderRadius: '20px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
  modalTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '24px', color: '#1e293b' },
  modalActions: { display: 'flex', gap: '12px', marginTop: '10px' },
  saveBtn: { flex: 1, padding: '12px', background: '#1a365d', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
  cancelBtnModal: { flex: 1, padding: '12px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' }
};

export default CommitteesSection;
